/**
 * Agent workspace extras (plan points 54–57): the hand-rolled throughput area
 * chart, the published-vs-draft prompt diff, the unified governance timeline,
 * and the guarded test-run panel. Rendered from AgentDetail; existing panels
 * stay untouched.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgentWorkspace } from '@servicesai/api-client';
import { GlassButton, StatusPill, useI18n } from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { S } from '../../strings.js';
import { IconBadge, SectionCard } from '../elevated/kit.js';
import { Icon, type IconName } from '../elevated/icons.js';
import { ElevatedEmptyState } from '../elevated/states.js';

interface ActivityRunRow {
  id: string;
  status: string;
  started_at: string;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
}

/* --------------------------------------------------------------------------
 * 54 — Throughput: dual-metric area chart (runs/day + tokens/day, 14 days).
 * ------------------------------------------------------------------------- */
const THROUGHPUT_DAYS = 14;
const GOLD = '#cba344';

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function ThroughputCard({ workspace, accent }: { workspace: AgentWorkspace; accent: string }) {
  const { l, lang } = useI18n();
  const days = useMemo(() => {
    const now = new Date();
    const buckets: { key: string; date: Date; runs: number; tokens: number }[] = [];
    for (let offset = THROUGHPUT_DAYS - 1; offset >= 0; offset -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
      buckets.push({ key: dayKey(date), date, runs: 0, tokens: 0 });
    }
    const byKey = new Map(buckets.map((bucket) => [bucket.key, bucket]));
    for (const row of workspace.activity as unknown as ActivityRunRow[]) {
      const started = new Date(row.started_at);
      if (Number.isNaN(started.getTime())) continue;
      const bucket = byKey.get(dayKey(started));
      if (!bucket) continue;
      bucket.runs += 1;
      bucket.tokens += Number(row.prompt_tokens ?? 0) + Number(row.completion_tokens ?? 0);
    }
    return buckets;
  }, [workspace.activity]);

  const totalRuns = days.reduce((sum, day) => sum + day.runs, 0);
  const maxRuns = Math.max(1, ...days.map((day) => day.runs));
  const maxTokens = Math.max(1, ...days.map((day) => day.tokens));

  const width = 560;
  const height = 176;
  const pad = { top: 12, right: 10, bottom: 26, left: 10 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const x = (index: number) => pad.left + (index / (THROUGHPUT_DAYS - 1)) * innerWidth;
  const seriesPath = (values: number[], max: number) =>
    values
      .map((value, index) => `${x(index).toFixed(1)},${(pad.top + (1 - value / max) * innerHeight).toFixed(1)}`)
      .join(' ');
  const areaPath = (values: number[], max: number) =>
    `${pad.left},${pad.top + innerHeight} ${seriesPath(values, max)} ${pad.left + innerWidth},${pad.top + innerHeight}`;
  const runValues = days.map((day) => day.runs);
  const tokenValues = days.map((day) => day.tokens);
  const locale = lang === 'ar' ? 'ar-AE' : 'en-GB';

  return (
    <SectionCard title={l(S.throughput)} hint={l(S.throughputHint)} accent={accent}>
      {totalRuns === 0 ? (
        <ElevatedEmptyState icon="chart" title={l(S.throughputEmpty)} />
      ) : (
        <div dir="ltr">
          <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={l(S.throughput)}>
            <defs>
              <linearGradient id="throughput-runs-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity={0.32} />
                <stop offset="100%" stopColor={accent} stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="throughput-tokens-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GOLD} stopOpacity={0.3} />
                <stop offset="100%" stopColor={GOLD} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <line
              x1={pad.left}
              x2={pad.left + innerWidth}
              y1={pad.top + innerHeight}
              y2={pad.top + innerHeight}
              stroke="#e6e7ea"
              strokeWidth={1}
            />
            <polygon points={areaPath(tokenValues, maxTokens)} fill="url(#throughput-tokens-fill)" />
            <polyline
              points={seriesPath(tokenValues, maxTokens)}
              fill="none"
              stroke={GOLD}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <polygon points={areaPath(runValues, maxRuns)} fill="url(#throughput-runs-fill)" />
            <polyline
              points={seriesPath(runValues, maxRuns)}
              fill="none"
              stroke={accent}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {days.map((day, index) =>
              index % 2 === 0 ? (
                <text
                  key={day.key}
                  x={x(index)}
                  y={height - 8}
                  textAnchor="middle"
                  className="fill-aeblack-400"
                  fontSize={9}
                >
                  {day.date.toLocaleDateString(locale, { day: 'numeric', month: 'numeric' })}
                </text>
              ) : null,
            )}
          </svg>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-[11px] font-semibold text-ink-soft" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: accent }} />
              {l(S.runsPerDay)} · {days.reduce((max, day) => Math.max(max, day.runs), 0)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: GOLD }} />
              {l(S.tokensPerDay)} · {days.reduce((max, day) => Math.max(max, day.tokens), 0).toLocaleString('en-GB')}
            </span>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

/* --------------------------------------------------------------------------
 * 55 — Prompt diff: published vs draft, naive line-level LCS diff.
 * ------------------------------------------------------------------------- */
type DiffKind = 'same' | 'added' | 'removed' | 'changed';
interface DiffLine {
  text: string;
  kind: DiffKind;
}

function diffLines(leftText: string[], rightText: string[]): { left: DiffLine[]; right: DiffLine[] } {
  const m = leftText.length;
  const n = rightText.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      dp[i]![j] = leftText[i] === rightText[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const ops: { type: 'same' | 'del' | 'ins'; text: string }[] = [];
  let i = 0;
  let j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && leftText[i] === rightText[j]) {
      ops.push({ type: 'same', text: leftText[i]! });
      i += 1;
      j += 1;
    } else if (j < n && (i === m || dp[i]![j + 1]! >= dp[i + 1]![j]!)) {
      ops.push({ type: 'ins', text: rightText[j]! });
      j += 1;
    } else {
      ops.push({ type: 'del', text: leftText[i]! });
      i += 1;
    }
  }
  const left: DiffLine[] = [];
  const right: DiffLine[] = [];
  let k = 0;
  while (k < ops.length) {
    const op = ops[k]!;
    if (op.type === 'same') {
      left.push({ text: op.text, kind: 'same' });
      right.push({ text: op.text, kind: 'same' });
      k += 1;
      continue;
    }
    const block: { type: 'del' | 'ins'; text: string }[] = [];
    while (k < ops.length && ops[k]!.type !== 'same') {
      block.push(ops[k]! as { type: 'del' | 'ins'; text: string });
      k += 1;
    }
    const dels = block.filter((entry) => entry.type === 'del');
    const inss = block.filter((entry) => entry.type === 'ins');
    const changed = Math.min(dels.length, inss.length);
    for (let index = 0; index < changed; index += 1) {
      left.push({ text: dels[index]!.text, kind: 'changed' });
      right.push({ text: inss[index]!.text, kind: 'changed' });
    }
    for (let index = changed; index < dels.length; index += 1) left.push({ text: dels[index]!.text, kind: 'removed' });
    for (let index = changed; index < inss.length; index += 1) right.push({ text: inss[index]!.text, kind: 'added' });
  }
  return { left, right };
}

const DIFF_LINE_CLASS: Record<DiffKind, string> = {
  same: 'text-ink-soft',
  added: 'bg-aegreen-100 text-aegreen-700',
  removed: 'bg-aered-100 text-aered-600',
  changed: 'bg-aegold-100 text-aegold-700',
};

function DiffPanel({ title, lines }: { title: string; lines: DiffLine[] }) {
  return (
    <div className="min-w-0 rounded-xl border border-aeblack-100">
      <div className="border-b border-aeblack-100 bg-aeblack-50 px-3 py-2 text-xs font-extrabold">{title}</div>
      <div dir="ltr" className="max-h-96 overflow-auto py-1 font-mono text-[11px] leading-relaxed">
        {lines.map((line, index) => (
          <div key={index} className={`flex gap-2 px-3 ${DIFF_LINE_CLASS[line.kind]}`}>
            <span className="w-7 shrink-0 select-none text-end text-aeblack-300">{index + 1}</span>
            <code className="whitespace-pre-wrap break-all">{line.text || ' '}</code>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PromptDiffCard({ workspace }: { workspace: AgentWorkspace }) {
  const { l } = useI18n();
  const draft = workspace.versions.find((version) => version.state === 'draft') ?? null;
  const published = workspace.publishedVersion;
  const diff = useMemo(() => {
    if (!draft) return null;
    const publishedLines = published
      ? JSON.stringify(published.config.prompt, null, 2).split('\n').slice(0, 600)
      : [];
    const draftLines = JSON.stringify(draft.config.prompt, null, 2).split('\n').slice(0, 600);
    return diffLines(publishedLines, draftLines);
  }, [draft, published]);
  if (!draft || !diff) return null;
  return (
    <SectionCard
      title={l(S.promptDiff)}
      hint={l(S.promptDiffHint)}
      accent={GOLD}
      className="mt-4"
      actions={
        <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold text-ink-soft">
          <span className="inline-flex items-center gap-1"><span aria-hidden className="h-2 w-2 rounded-full bg-aegreen-600" />{l(S.diffAdded)}</span>
          <span className="inline-flex items-center gap-1"><span aria-hidden className="h-2 w-2 rounded-full bg-aered-600" />{l(S.diffRemoved)}</span>
          <span className="inline-flex items-center gap-1"><span aria-hidden className="h-2 w-2 rounded-full bg-aegold-400" />{l(S.diffChanged)}</span>
        </div>
      }
    >
      {!published && <p className="mb-3 text-xs text-ink-faint">{l(S.noPublishedPrompt)}</p>}
      <div className="grid gap-4 lg:grid-cols-2">
        <DiffPanel title={`${l(S.published)} · v${published?.version ?? '—'}`} lines={diff.left} />
        <DiffPanel title={`${l(S.draft)} · v${draft.version}`} lines={diff.right} />
      </div>
    </SectionCard>
  );
}

/* --------------------------------------------------------------------------
 * 56 — Unified governance timeline: version events + approvals + audit rows.
 * ------------------------------------------------------------------------- */
interface TimelineItem {
  id: string;
  at: string;
  icon: IconName;
  accent: string;
  label: string;
  actor: string;
  mono?: boolean;
  pill?: string;
}

interface WorkspaceApprovalRow {
  id: string;
  state: string;
  maker_id: string;
  created_at: string;
}

interface AgentAuditEntry {
  id: number;
  ts: string;
  actor: Record<string, unknown>;
  action: string;
}

export function GovernanceTimelineCard({
  slug,
  workspace,
  accent,
}: {
  slug: string;
  workspace: AgentWorkspace;
  accent: string;
}) {
  const { l, lang } = useI18n();
  const auditQuery = useQuery({
    queryKey: ['agent-governance-audit', slug],
    queryFn: () => admin().agentAudit(slug, { limit: 20 }),
  });
  const items = useMemo(() => {
    const feed: TimelineItem[] = [];
    for (const version of workspace.versions) {
      feed.push({
        id: `version-${version.id}-created`,
        at: version.created_at,
        icon: 'builder',
        accent: GOLD,
        label: `${l(S.timelineDraftCreated)} · v${version.version}`,
        actor: version.created_by,
        mono: true,
      });
      if (version.submitted_at) {
        feed.push({
          id: `version-${version.id}-submitted`,
          at: version.submitted_at,
          icon: 'approvals',
          accent: '#0173ab',
          label: `${l(S.timelineSubmitted)} · v${version.version}`,
          actor: version.created_by,
          mono: true,
        });
      }
      if (version.published_at) {
        feed.push({
          id: `version-${version.id}-published`,
          at: version.published_at,
          icon: 'check',
          accent: '#3f8e50',
          label: `${l(S.timelinePublished)} · v${version.version}`,
          actor: version.published_by ?? version.created_by,
          mono: true,
        });
      }
      if (version.state === 'rejected') {
        feed.push({
          id: `version-${version.id}-rejected`,
          at: version.updated_at,
          icon: 'x',
          accent: '#b52520',
          label: `${l(S.timelineRejected)} · v${version.version}`,
          actor: version.published_by ?? version.created_by,
          mono: true,
        });
      }
    }
    for (const approval of workspace.approvals as unknown as WorkspaceApprovalRow[]) {
      feed.push({
        id: `approval-${approval.id}`,
        at: approval.created_at,
        icon: 'shield',
        accent: '#0173ab',
        label: l(S.timelineApproval),
        actor: approval.maker_id,
        mono: true,
        pill: approval.state,
      });
    }
    for (const entry of auditQuery.data?.items ?? []) {
      const audit = entry as AgentAuditEntry;
      feed.push({
        id: `audit-${audit.id}`,
        at: audit.ts,
        icon: 'audit',
        accent: '#3e4046',
        label: audit.action,
        actor: `${String(audit.actor.kind ?? 'system')} · ${String(audit.actor.id ?? '—')}`,
        mono: true,
      });
    }
    return feed
      .filter((item) => !Number.isNaN(new Date(item.at).getTime()))
      .sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime())
      .slice(0, 40);
  }, [workspace.versions, workspace.approvals, auditQuery.data, l]);

  return (
    <SectionCard title={l(S.governanceTimeline)} hint={l(S.governanceTimelineHint)} accent={accent}>
      {items.length === 0 ? (
        <ElevatedEmptyState icon="audit" title={l(S.timelineEmpty)} />
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.id} className="flex items-start gap-3">
              <IconBadge accent={item.accent} size={30}>
                <Icon name={item.icon} size={14} />
              </IconBadge>
              <div className="min-w-0 flex-1 border-b border-aeblack-50 pb-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-xs font-bold ${item.mono && item.id.startsWith('audit-') ? 'font-mono' : ''}`}>{item.label}</span>
                  {item.pill && (
                    <StatusPill
                      state={item.pill === 'approved' ? 'published' : item.pill === 'rejected' ? 'rejected' : 'in_review'}
                      label={item.pill}
                    />
                  )}
                </div>
                <div className="mt-0.5 truncate font-mono text-[10px] text-ink-faint" dir="ltr">{item.actor}</div>
              </div>
              <time className="shrink-0 text-[10px] text-ink-faint">
                {new Date(item.at).toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-GB')}
              </time>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/* --------------------------------------------------------------------------
 * 57 — Guarded test-run panel (back-office agents). admin().runAgent accepts
 * an optional params payload, so the custom input is passed as { input }.
 * ------------------------------------------------------------------------- */
export function TestRunCard({ slug }: { slug: string }) {
  const { l } = useI18n();
  const queryClient = useQueryClient();
  const [input, setInput] = useState('');
  const testMutation = useMutation({
    mutationFn: (value: string) => admin().runAgent(slug, value ? { input: value } : undefined),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agent-detail', slug] }),
        queryClient.invalidateQueries({ queryKey: ['agent-workspace', slug] }),
        queryClient.invalidateQueries({ queryKey: ['agent-portfolio'] }),
      ]);
    },
  });
  const lastStatus = testMutation.data?.status ?? null;
  return (
    <SectionCard title={l(S.testRun)} hint={l(S.testRunHint)} accent="#3f8e50">
      <textarea
        value={input}
        rows={3}
        dir="auto"
        disabled={testMutation.isPending}
        placeholder={l(S.testRunPlaceholder)}
        aria-label={l(S.testRunPlaceholder)}
        onChange={(event) => setInput(event.target.value)}
        className="glass w-full rounded-xl p-3 text-sm focus:outline-2 focus:outline-gold/70 disabled:opacity-60"
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <GlassButton
          variant="primary"
          size="sm"
          disabled={testMutation.isPending}
          onClick={() => testMutation.mutate(input.trim())}
        >
          {testMutation.isPending ? l(S.running) : l(S.runTest)}
        </GlassButton>
        {lastStatus && (
          <span className="inline-flex items-center gap-2 text-xs text-ink-soft">
            <span className="text-ink-faint">{l(S.testRunLastStatus)}:</span>
            <StatusPill
              state={lastStatus === 'succeeded' ? 'published' : lastStatus === 'failed' ? 'rejected' : 'in_progress'}
              label={lastStatus}
            />
          </span>
        )}
      </div>
      {testMutation.data?.error && (
        <p className="mt-2 whitespace-pre-wrap rounded-lg bg-aered-50 p-2 text-xs text-reject">{testMutation.data.error}</p>
      )}
      {testMutation.error && (
        <div className="aegov-alert alert-error mt-3" role="alert">
          <div className="alert-content">{l(S.actionFailed)}: {(testMutation.error as Error).message}</div>
        </div>
      )}
    </SectionCard>
  );
}
